import React, { FC } from 'react';
import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link as RouterLink } from 'react-router-dom';

import {
  CurrentUserDraftAndPendingIdeasQuery,
  CurrentUserWhitePaperDraftsQuery,
  useCurrentUserDraftAndPendingIdeasQuery,
  useCurrentUserWhitePaperDraftsQuery,
  useDeleteWhitePaperDraftMutation,
  useDestroyIdeaDraftMutation
} from 'interfaces/graphql-typings';

import styles from './researchDraftsStyles.scss';

const IdeaDraftItem: FC<{
  idea: NonNullable<
    NonNullable<CurrentUserDraftAndPendingIdeasQuery['currentUser']>['draftAndPendingIdeas']
  >[number];
}> = ({ idea }) => {
  const [destroyIdeaDraft, { loading }] = useDestroyIdeaDraftMutation({
    update: (cache, { data }) => {
      if (!data?.destroyIdeaDraft) return;

      cache.modify({
        id: cache.identify({
          id: data.destroyIdeaDraft.currentUserId,
          __typename: 'User'
        }),
        fields: {
          draftIdeas: (cachedIdeas, { readField }) =>
            cachedIdeas.filter(
              (
                idea: NonNullable<
                  NonNullable<
                    CurrentUserDraftAndPendingIdeasQuery['currentUser']
                  >['draftAndPendingIdeas']
                >[number]
              ) => data.destroyIdeaDraft?.ideaId !== readField('id', idea)
            )
        }
      });
    }
  });

  const { id: ideaId, positions, updatedAt, status } = idea;

  const relativeEditPath = `idea/${ideaId}`;

  return (
    <div className={styles.draftItem}>
      <div className={styles.draftItemTitle}>
        {positions.map((position, index) => (
          <RouterLink key={index} to={loading ? '#' : relativeEditPath}>
            {position.asset?.entity.name}{' '}
            {position.asset?.primarySymbol ? `(${position.asset?.primarySymbol})` : ''}
          </RouterLink>
        ))}
        {status === 'open' ? <label>Pending Review</label> : null}
        <small>{updatedAt}</small>
      </div>
      {status === 'draft' ? (
        <button
          className={styles.deleteButton}
          data-testid="delete-button"
          disabled={loading}
          onClick={() => destroyIdeaDraft({ variables: { ideaId } })}
        >
          <FontAwesomeIcon spin={loading} icon={loading ? faSpinner : faTrash} />
        </button>
      ) : null}
    </div>
  );
};

const WhitePaperDraftItem: FC<{
  whitePaper: NonNullable<
    NonNullable<CurrentUserWhitePaperDraftsQuery['currentUser']>['draftWhitePapers']
  >[number];
}> = ({ whitePaper }) => {
  const [destroyWhitePaperDraft, { loading }] = useDeleteWhitePaperDraftMutation({
    update: (cache, { data }) => {
      if (!data?.destroyWhitePaperDraft) return;

      cache.modify({
        id: cache.identify({
          id: data.destroyWhitePaperDraft.currentUserId,
          __typename: 'User'
        }),
        fields: {
          draftWhitePapers: (cachedWhitePapers, { readField }) =>
            cachedWhitePapers.filter(
              (
                whitePaper: NonNullable<
                  NonNullable<CurrentUserWhitePaperDraftsQuery['currentUser']>['draftWhitePapers']
                >[number]
              ) => data.destroyWhitePaperDraft?.whitePaperId !== readField('id', whitePaper)
            )
        }
      });
    }
  });

  const { id: whitePaperId, categories, title: whitePaperTitle, updatedAt } = whitePaper;

  const relativeEditPath = `macro_thematic/${whitePaperId}`;

  return (
    <div className={styles.draftItem}>
      <div className={styles.draftItemTitle}>
        <RouterLink to={loading ? '#' : relativeEditPath}>{whitePaperTitle}</RouterLink>
        <p>{categories.join(', ')}</p>
        <small>{updatedAt}</small>
      </div>
      <button
        className={styles.deleteButton}
        data-testid="delete-button"
        disabled={loading}
        onClick={() => destroyWhitePaperDraft({ variables: { whitePaperId } })}
      >
        <FontAwesomeIcon spin={loading} icon={loading ? faSpinner : faTrash} />
      </button>
    </div>
  );
};

export const IdeaDrafts: FC = () => {
  const { data } = useCurrentUserDraftAndPendingIdeasQuery({
    fetchPolicy: 'network-only'
  });

  const draftAndPendingIdeas = data?.currentUser?.draftAndPendingIdeas;

  if (!draftAndPendingIdeas?.length) return null;

  return (
    <div className={styles.researchDrafts}>
      <p data-drafts-title>Your Drafts</p>
      <p>Select a recent draft below to continue editing.</p>
      <div className={styles.draftItems}>
        {draftAndPendingIdeas.map(idea => (
          <IdeaDraftItem key={'draft-idea-' + idea.id} idea={idea} />
        ))}
      </div>
    </div>
  );
};

export const WhitePaperDrafts: FC = () => {
  const { data } = useCurrentUserWhitePaperDraftsQuery({
    fetchPolicy: 'network-only'
  });

  const draftWhitePapers = data?.currentUser?.draftWhitePapers;

  if (!draftWhitePapers?.length) return null;

  return (
    <div className={styles.researchDrafts}>
      <p data-drafts-title>Your Drafts</p>
      <p>Select a recent draft below to continue editing.</p>
      <div className={styles.draftItems}>
        {draftWhitePapers.map(whitePaper => (
          <WhitePaperDraftItem key={'draft-white-paper-' + whitePaper.id} whitePaper={whitePaper} />
        ))}
      </div>
    </div>
  );
};
